import React, { PropsWithChildren } from 'react';
import { CrossIcon, SvgComponent } from '../../assets';
import styled from 'styled-components';
import { breakpoint, theme } from '../../theme';
import { Heading } from '../typography';

export type SimpleModalProps = PropsWithChildren<{
  onClose?: () => void;
  title?: string | SvgComponent | JSX.Element | Element;
  titlePosition?: TitlePosition;
  className?: string;
  icon?: React.ReactNode;
  iconFill?: string;
  iconWidth?: number;
  isVisible?: boolean;
  animationDuration?: number;
}>;

type TitleProps = {
  titlePosition: TitlePosition;
};

type TitlePosition = 'left' | 'center' | 'right';

type StyledModalProps = {
  isVisible: boolean;
  animationDuration: number;
};

const StyledModal = styled.div<StyledModalProps>`
  width: 375px;
  background-color: ${theme.colors.primary.white};
  padding: ${theme.spacings[5].rem};
  padding-top: 44px;
  box-sizing: border-box;
  position: absolute;
  font-family: ${theme.fonts.roboto};
  border-radius: ${theme.borderRadius.default};
  transform: ${({ isVisible }) => (isVisible ? 'scaleY(1)' : 'scaleY(0)')};
  transition: ${({ animationDuration }) =>
    `transform ${animationDuration}ms ease`};

  ${breakpoint('sm')`
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
  `};
`;

const ModalHeader = styled(Heading).attrs({ size: 4 })``;

const Cross = styled(CrossIcon)`
  color: ${theme.colors.primary.graysGray};
  position: absolute;
  top: ${theme.spacings[6.5].rem};
  right: ${theme.spacings[5].rem};
`;

const IconContainer = styled.div`
  position: absolute;
  top: 9px;
  left: 18px;
`;

const Title = styled.div<TitleProps>`
  width: 100%;
  text-align: ${({ titlePosition }) => titlePosition};
`;

const StyledTitle = styled(Title)`
  margin-top: ${theme.spacings[8].rem};
`;

export const SimpleModal: React.FC<SimpleModalProps> = ({
  onClose,
  title,
  children,
  titlePosition = 'left',
  className,
  icon,
  isVisible = false,
  animationDuration = 200,
}) => {
  return (
    <StyledModal
      isVisible={isVisible}
      animationDuration={animationDuration}
      className={className}
    >
      <ModalHeader>
        {icon ? (
          <>
            <IconContainer>{icon}</IconContainer>
            <StyledTitle titlePosition={titlePosition}>
              <>{title}</>
            </StyledTitle>
          </>
        ) : (
          <Title titlePosition={titlePosition}>
            <>{title}</>
          </Title>
        )}

        <Cross onClick={onClose} width={16} />
      </ModalHeader>

      {children}
    </StyledModal>
  );
};
